import { AdductionOrder } from '@features/siteStatusV2/types';
import { OrderStepStatus } from '@features/siteStatusV2/types/order/enums';
import theme from '@theme';
import React from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import { Icon } from '../icon';
import StepsAccordion from './StepsAccordion';

interface StepstModal {
  order: AdductionOrder | null;
  setSelectedOrder: React.Dispatch<React.SetStateAction<AdductionOrder | null>>;
}

const StepstModal: React.FC<StepstModal> = ({ order, setSelectedOrder }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);

  const countFinishedOrInProgressSteps = (order: AdductionOrder): number => {
    const steps = [
      order.fieldVisit?.stepStatus,
      order.quote?.stepStatus,
      order.works?.stepStatus,
      order.orderCompleted?.stepStatus
    ];

    return steps.filter(
      (status) => status === OrderStepStatus.FINISHED || status === OrderStepStatus.IN_PROGRESS
    ).length;
  };

  const stepsProgress = order ? `${countFinishedOrInProgressSteps(order)}/4` : '';

  return (
    <Modal show={!!order} onHide={() => setSelectedOrder(null)}>
      <ModalHeader>
        <Icon
          iconName="chevron-double-left"
          className="icon"
          onClick={() => setSelectedOrder(null)}
        />
        <ModalTitle>{order?.name}</ModalTitle>
      </ModalHeader>
      <Modal.Body>
        <TitleBody>
          {t('orderDetails.adduction.steps')}
          <Steps>{stepsProgress}</Steps>
        </TitleBody>
        <StepsAccordion order={order} />
      </Modal.Body>
    </Modal>
  );
};

const slideIn = keyframes`
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  `;

const slideOut = keyframes`
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  `;

const Modal = styled(BootstrapModal)`
  .modal-dialog {
    height: 100vh;
    max-width: 100%;
    width: 25vw;
    margin: 0;
    position: fixed;
    right: 0;
    top: 0;
    animation: ${({ show }: { show: boolean }) => (show ? slideIn : slideOut)} 0.7s ease-out;
  }

  .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    border: none;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
  }
`;

const ModalHeader = styled(Modal.Header)`
  justify-content: flex-start;
  border: none;
  background: linear-gradient(180deg, #e7f0ff 0%, rgba(255, 255, 255, 0) 100%);

  .icon {
    stroke: ${theme.palette.grey[400]};
    cursor: pointer;
    margin-right: 0.75rem;
    width: 11px;
  }
`;

const ModalTitle = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.blue[500]};
`;

const TitleBody = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[800]};
  display: flex;
`;

const Steps = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[400]};
  margin-left: 0.25rem;
`;

export default StepstModal;
